import { action, computed, makeObservable, observable } from "mobx";
import { v4 as uuidv4 } from "uuid";
import { MessageAuthor } from "./MessageAuthor";
import type { ChatActionsMap } from "./ChatAction";
import { TemplatedChatMessage, ChatMessage } from "./ChatMessage";
import type { IChatMessage } from "./ChatMessage";
import { fromServerSuggestion } from "./ChatSuggestion";
import type {
    ChatSuggestion,
    ChatServerSuggestion,
    ChatSuggestAction,
} from "./ChatSuggestion";
import { ChatActions } from "./ChatAction";
import { Chat } from "./Chat";
import { HasParent } from "./Parent";
import type { FunctionCall } from "components/Admin/Organizations/OrganizationDetail/OrganizationChatReview/types";
import type { AgentArtifact } from "./AgentArtifact";

export type ReasoningSummary = {
    modelTurn: number;
    timestampUtc: string;
    text: string;
};

export interface IChatMessageGroup {
    readonly id: string;
    author: MessageAuthor;
    messages: IChatMessage[];
    suggestions?: ChatSuggestion[];
    actions?: ChatActions;
    transientMessage?: IChatMessage;
    functionCalls?: FunctionCall[];
    artifacts?: AgentArtifact[];
    userFeedbackType?: string;
    userFeedback?: string;
}

/**
 * Represents a collection of messages sent by an individual author.
 */
export class ChatMessageGroup implements IChatMessageGroup, HasParent<Chat> {
    public readonly id: string;

    public readonly parent: Chat;

    @observable author: MessageAuthor;
    @observable messages: IChatMessage[];
    @observable suggestions?: ChatSuggestion[];
    @observable actions: ChatActions;
    @observable transientMessage?: IChatMessage;
    @observable functionCalls?: FunctionCall[];
    @observable artifacts?: AgentArtifact[];
    // Feedback belongs to the last message in this group.
    @observable userFeedbackType?: string;
    @observable userFeedback?: string;
    private previousSuggestions?: ChatSuggestion[];

    constructor(chat: Chat, author: MessageAuthor) {
        makeObservable(this);

        this.parent = chat;

        this.id = uuidv4();
        this.author = author;
        this.messages = [];
        this.suggestions = [];
        this.actions = new ChatActions();
    }

    @computed get recentMessage(): IChatMessage | undefined {
        return this.messages.at(-1);
    }

    public addMessage(message: IChatMessage): IChatMessage;
    public addMessage(content: string): IChatMessage;
    @action
    public addMessage(messageOrContent: string | IChatMessage): IChatMessage {
        let message: IChatMessage;

        if (typeof messageOrContent === "string")
            message = new ChatMessage(this, messageOrContent);
        else message = messageOrContent;

        this.setFeedback();
        this.messages.push(message);
        return message;
    }

    @action
    public setActions(actions: ChatActionsMap) {
        this.actions = ChatActions.from(actions);
    }

    public addSuggestion(suggestion: ChatSuggestion): ChatSuggestion;
    public addSuggestion(
        content: string,
        action: ChatSuggestAction,
    ): ChatSuggestion;
    @action
    public addSuggestion(
        suggestionOrContent: string | ChatSuggestion,
        action?: ChatSuggestAction,
    ): ChatSuggestion {
        let suggestion: ChatSuggestion;

        if (typeof suggestionOrContent === "string") {
            if (!action) throw new Error("An action must be provided");
            suggestion = { content: suggestionOrContent, action };
        } else suggestion = suggestionOrContent;

        if (!this.suggestions) this.suggestions = [];
        this.suggestions.push(suggestion);
        return suggestion;
    }

    @action
    public addServerSuggestion(
        serverSuggestion: ChatServerSuggestion,
    ): ChatSuggestion | undefined {
        const suggestion = fromServerSuggestion(serverSuggestion, this);
        if (!this.suggestions) this.suggestions = [];
        if (!suggestion) return;
        this.suggestions.push(suggestion);
        return suggestion;
    }

    @action
    public addServerSuggestions(
        serverSuggestions: ChatServerSuggestion[],
    ): ChatSuggestion[] {
        const suggestions: ChatSuggestion[] = [];
        for (const serverSuggestion of serverSuggestions) {
            const suggestion = this.addServerSuggestion(serverSuggestion);
            if (!suggestion) {
                continue;
            }
            suggestions.push(suggestion);
        }
        return suggestions;
    }

    @action
    public setSuggestions(suggestions: ChatSuggestion[]) {
        this.suggestions = suggestions;
    }

    @action
    public clearSuggestions() {
        this.suggestions = [];
    }

    @action
    public setTransientMessage(
        content: string,
        suggestions?: ChatSuggestion[],
    ) {
        this.showTransientMessage(new ChatMessage(this, content), suggestions);
    }

    @action public setTransientMessageTemplated(
        bindingObject: unknown,
        buildContent: (arg: unknown) => string,
        suggestions?: ChatSuggestion[],
    ) {
        this.showTransientMessage(
            new TemplatedChatMessage(this, bindingObject, buildContent),
            suggestions,
        );
    }

    /**
     * Transient suggestions temporarily replace the group's own, which are
     * stashed so `clearTransientMessage` can put them back.
     */
    private showTransientMessage(
        message: IChatMessage,
        suggestions?: ChatSuggestion[],
    ) {
        this.clearTransientMessage();
        this.transientMessage = message;
        if (suggestions) {
            this.previousSuggestions = this.suggestions;
            this.suggestions = suggestions;
        }
    }

    /**
     * If a transient message is set, it is added to the `messages` array and
     * its suggestions join the group's own.
     */
    @action
    public commitTransientMessage() {
        const message = this.transientMessage;
        if (!message) return;
        this.addMessage(message);
        this.suggestions = [
            ...(this.previousSuggestions ?? []),
            ...(this.suggestions ?? []),
        ];
        this.previousSuggestions = undefined;
        this.transientMessage = undefined;
        return message;
    }

    /** Drops the transient message, restoring any suggestions it replaced. */
    @action
    public clearTransientMessage() {
        this.transientMessage = undefined;
        if (this.previousSuggestions) {
            this.suggestions = this.previousSuggestions;
            this.previousSuggestions = undefined;
        }
    }

    /** Undoes `commitTransientMessage` when what the message was for didn't happen. */
    @action
    public restoreTransientMessage(message: IChatMessage) {
        this.messages = this.messages.filter((existing) => existing !== message);
        this.transientMessage = message;
    }

    @action
    public setFeedback(userFeedbackType?: string, userFeedback?: string) {
        this.userFeedbackType = userFeedbackType;
        this.userFeedback = userFeedback;
    }

    @action
    public setFunctionCalls(functionCalls: FunctionCall[]) {
        this.functionCalls = functionCalls;
    }

    @action
    public addArtifacts(artifacts: AgentArtifact[]) {
        this.artifacts = [...(this.artifacts ?? []), ...artifacts];
    }
}
